import React from "react";
import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";
import foodPlaceholder from "@/assets/product-placeholder.png";
import { useTheme } from "@mui/material/styles";
import type { BaseAtomProps } from "../../types";

export interface ImageProps extends BaseAtomProps {
  /**
   * Image source URL
   */
  src: string;

  /**
   * Alternative text for accessibility
   */
  alt: string;

  /**
   * Image width (used for both Cloudinary optimization and CSS)
   */
  width?: number;

  /**
   * Image height (used for both Cloudinary optimization and CSS)
   */
  height?: number;

  /**
   * Make image responsive (fit to parent width with square aspect ratio)
   */
  responsive?: boolean;

  /**
   * Loading behavior
   */
  loading?: "lazy" | "eager";

  /**
   * CSS object-fit property
   */
  objectFit?: "cover" | "contain" | "fill" | "scale-down" | "none";

  /**
   * CSS object-position property
   */
  objectPosition?: string;

  /**
   * Fallback image URL
   */
  fallbackSrc?: string;

  /**
   * Cloudinary crop mode
   */
  crop?: string;

  /**
   * Cloudinary gravity setting
   */
  gravity?: string;

  /**
   * Cloudinary quality setting
   */
  quality?: string | number;

  /**
   * Cloudinary format setting
   */
  format?: string;

  /**
   * Whether to optimize with Cloudinary
   */
  optimizeWithCloudinary?: boolean;

  /**
   * Enable loading state with opacity transition
   */
  showLoadingState?: boolean;

  /**
   * Custom border radius
   */
  borderRadius?: number | string;

  /**
   * Custom styles
   */
  sx?: SxProps<Theme>;

  /**
   * Load callback
   */
  onLoad?: () => void;

  /**
   * Error callback
   */
  onError?: (error: Error) => void;

  /**
   * MUI component to render as
   */
  component?: React.ElementType;
}

/**
 * Image Component Usage Examples:
 *
 * // Responsive square image (fits parent width, maintains 1:1 aspect ratio)
 * <Image src={url} alt="Product" width={260} height={260} responsive />
 *
 * // Fixed size image (for avatars, icons)
 * <Image src={url} alt="Avatar" width={50} height={50} />
 *
 * // Responsive image without fixed dimensions
 * <Image src={url} alt="Banner" responsive />
 */

// Global cache for optimized image URLs
const imageCache = new Map<string, string>();

/**
 * Generate cache key for image optimization
 */
const generateCacheKey = (
  src: string,
  width?: number,
  height?: number,
  crop?: string,
  quality?: string | number,
  gravity?: string,
  format?: string
): string => {
  return `${src}|${width || 0}|${height || 0}|${crop || "fill"}|${
    quality || "auto"
  }|${gravity || "auto"}|${format || "auto"}`;
};

/**
 * Custom hook for Cloudinary image optimization with caching
 */
const useOptimizedImage = (
  src: string,
  options: {
    width?: number;
    height?: number;
    optimizeWithCloudinary?: boolean;
  } = {}
) => {
  const [optimizedUrl, setOptimizedUrl] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!src || options.optimizeWithCloudinary === false) {
      setOptimizedUrl(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const cacheKey = generateCacheKey(src, options.width, options.height);

    // Check cache first
    if (imageCache.has(cacheKey)) {
      setOptimizedUrl(imageCache.get(cacheKey)!);
      setIsLoading(false);
      setError(null);
      return;
    }

    // setIsLoading(true);
    // setError(null);

    // cloudinaryService
    //   .smartTransformImage(src, {
    //     width: options.width,
    //     height: options.height,
    //     crop: options.crop || "fill",
    //     quality: options.quality || "auto",
    //     gravity: options.gravity || "auto",
    //     format: options.format || "auto",
    //   })
    //   .then((result) => {
    //     // Cache the result
    //     imageCache.set(cacheKey, result.url);
    //     if (process.env.NODE_ENV === "development") {
    //       console.log("🖼️ Image cached:", cacheKey.substring(0, 50) + "...");
    //     }
    //     setOptimizedUrl(result.url);
    //     setIsLoading(false);
    //   })
    //   .catch((err) => {
    //     console.error("Image optimization failed:", err);
    //     setError(err);
    //     setIsLoading(false);
    //     // Cache the original URL as fallback
    //     imageCache.set(cacheKey, src);
    //     setOptimizedUrl(src);
    //   });
  }, [src, options.width, options.height, options.optimizeWithCloudinary]);

  return { optimizedUrl, isLoading, error };
};

/**
 * Whether the source URL is empty or invalid
 */
const isEmptySource = (src: string | null | undefined): boolean => {
  return !src || src.trim() === "";
};

const ImageComponent: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  responsive = false,
  loading = "lazy",
  objectFit = "cover",
  objectPosition = "center",
  fallbackSrc,
  optimizeWithCloudinary = true,
  showLoadingState = true,
  borderRadius,
  sx,
  onLoad,
  onError,
  component = "img",
  className,
  "data-testid": dataTestId,
  ...props
}) => {
  const theme = useTheme();

  // Состояние для отслеживания ошибки загрузки изображения
  const [hasError, setHasError] = React.useState<boolean>(false);

  // Check if source is empty
  const isEmpty = isEmptySource(src);

  // Use optimized image if available
  const { optimizedUrl, isLoading, error } = useOptimizedImage(
    isEmpty ? "" : src,
    {
      width,
      height,
      optimizeWithCloudinary: !isEmpty && optimizeWithCloudinary,
    }
  );

  // Определяем финальный URL изображения
  // Если была ошибка загрузки, используем fallback или foodPlaceholder
  const imageSrc = hasError
    ? fallbackSrc || foodPlaceholder
    : optimizedUrl || src || fallbackSrc || foodPlaceholder;

  // Сбрасываем состояние ошибки при изменении src
  React.useEffect(() => {
    setHasError(false);
  }, [src]);

  const handleLoad = React.useCallback(() => {
    setHasError(false);
    onLoad?.();
  }, [onLoad]);

  const handleError = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const target = e.currentTarget;
      const failedSrc = target.src;

      // if (process.env.NODE_ENV === "development") {
      //   console.log("🖼️ Image Error:", {
      //     failedSrc: failedSrc?.substring(0, 50) + "...",
      //     originalSrc: src?.substring(0, 50) + "...",
      //   });
      // }

      // Переключаемся на fallback только если еще не используем его
      // И если это не сам foodPlaceholder (чтобы избежать бесконечного цикла)
      const isPlaceholder =
        failedSrc.includes("food_placeholder") || failedSrc === foodPlaceholder;

      // Переключаемся на fallback только если это не placeholder и еще не переключились
      if (!isPlaceholder) {
        setHasError((prevHasError) => {
          // Переключаемся только если еще не переключились
          if (!prevHasError) {
            return true;
          }
          return prevHasError;
        });

        // Вызываем колбэк с ошибкой
        if (error) {
          onError?.(error);
        } else {
          onError?.(new Error(`Failed to load image: ${src}`));
        }
      }
    },
    [error, onError, src]
  );

  // Determine CSS dimensions
  let cssWidth: string | number;
  let cssHeight: string | number;

  if (responsive) {
    // Responsive mode: fit to parent width with square aspect ratio
    cssWidth = "100%";
    cssHeight = "auto";
  } else if (width || height) {
    // Use provided dimensions
    cssWidth = width ? `${width}px` : "100%";
    cssHeight = height ? `${height}px` : "auto";
  } else {
    // Default: fit to parent
    cssWidth = "100%";
    cssHeight = "auto";
  }

  const baseStyles: SxProps<Theme> = {
    width: cssWidth,
    height: cssHeight,
    borderRadius: borderRadius || 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: theme.transitions.create(["opacity"], {
      duration: theme.transitions.duration.short,
    }),
    opacity: showLoadingState && isLoading ? 0.7 : 1,
    position: "relative",
    overflow: "hidden",
    // Square aspect ratio for responsive mode
    ...(responsive && {
      aspectRatio: "1 / 1",
    }),
    // Merge with provided sx
    ...sx,
  };

  // Если источник пустой или произошла ошибка загрузки, показываем placeholder
  const shouldShowPlaceholder =
    isEmpty || (hasError && imageSrc === foodPlaceholder);

  if (shouldShowPlaceholder) {
    return (
      <Box
        component={component === "img" ? "div" : component}
        sx={{
          backgroundColor: "background.default",
          ...baseStyles,
        }}
        className={className}
        data-testid={dataTestId}
        {...props}
      >
        <Box
          component="img"
          src={foodPlaceholder}
          alt={alt}
          sx={{
            width: "80%",
            height: "80%",
            objectFit: "contain",
            opacity: 0.6,
          }}
        />
      </Box>
    );
  }

  const imageStyles: SxProps<Theme> = {
    ...baseStyles,
    objectFit,
    objectPosition,
  };

  if (component === "img") {
    return (
      <Box
        component="img"
        src={imageSrc || foodPlaceholder}
        alt={alt}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        sx={imageStyles}
        className={className}
        data-testid={dataTestId}
        {...props}
      />
    );
  }

  // For other components (like Box with background image)
  // Определяем URL для backgroundImage с учетом ошибок
  const backgroundImageUrl = hasError
    ? foodPlaceholder
    : imageSrc || foodPlaceholder;

  return (
    <Box
      component={component}
      sx={{
        ...imageStyles,
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: objectFit,
        backgroundPosition: objectPosition,
        backgroundRepeat: "no-repeat",
        position: "relative",
      }}
      className={className}
      data-testid={dataTestId}
      {...props}
    >
      {/* Скрытый img для отслеживания ошибок загрузки backgroundImage */}
      {!isEmpty && (
        <Box
          component="img"
          src={imageSrc || ""}
          alt=""
          onError={handleError}
          onLoad={handleLoad}
          sx={{
            position: "absolute",
            width: 0,
            height: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        />
      )}
    </Box>
  );
};

// Apply memoization to prevent unnecessary re-renders
export const Image = React.memo(ImageComponent, (prevProps, nextProps) => {
  return (
    prevProps.src === nextProps.src &&
    prevProps.alt === nextProps.alt &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.responsive === nextProps.responsive &&
    prevProps.optimizeWithCloudinary === nextProps.optimizeWithCloudinary
  );
});

// Set display name for debugging
(Image as any).displayName = "Image";
